import {
  useState,
  useMemo,
  useCallback,
  lazy,
  Suspense,
  useRef,
  useEffect,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTenant } from "../contexts/TenantContext";
import { Property, SearchFilters } from "../types/search";
import { useSearchQuery } from "../hooks/search/useSearchQuery";
import { useDirectSearchQuery } from "../hooks/search/useDirectSearchQuery";
import { LandingSearchBox } from "../components/features/search/LandingSearchBox";
import SearchResults from "../components/features/search/SearchResults";
import type { ViewMode } from "../components/features/search/ViewToggle";
import ViewToggle from "../components/features/search/ViewToggle";
import { Loader } from "lucide-react";
import { analyticsService } from "../services/analyticsService";
import { useMLSLogos } from "../hooks/mls/useMLSLogos";

// Lazy load the MapView component
const MapView = lazy(() => import("../components/features/search/MapView"));
import {
  generateHumanReadableFilters,
  generatePageTitle,
  generatePageDescription,
} from "../utils/metaTagUtils";
import { useServerFetchedProperties } from "../utils/serverPropertiesUtils";
import {
  loadMoreAfterServerProperties,
  buildApiFilters,
  resolveSearchCity,
} from "../utils/searchTransitionUtils";

/**
 * AdLandingPage - A landing page that uses URL query parameters for search
 *
 * URL format: /lp?q=homesforsale&city=Orlando&state=FL
 * New URL format: /lp/title=Homes+for+sale&subdivision
 *
 * Optional parameters:
 * - city: The city to search in (defaults to tenant's city if not provided)
 * - state: The state to search in (defaults to tenant's state if not provided)
 *
 * Other optional parameters:
 * - q: The search query
 * - title: The title to display on the page (used in h1)
 * - subdivision: The subdivision to search in (can be multiple)
 * - bedrooms: Number of bedrooms
 * - bathrooms: Number of bathrooms
 * - min_price: Minimum price
 * - max_price: Maximum price
 * - property_type: Property type (e.g., "Single Family Residence", "Condominium", "Townhouse", "Land")
 * - status: Listing status (e.g., "Active", "Pending", "Closed") (can be multiple)
 * - zip_code: ZIP code to search in
 *
 * Boolean features (true/false):
 * - waterfront: Property is on waterfront
 * - pool: Property has a pool
 * - basement: Property has a basement
 * - garage: Property has a garage
 * - newconstruction: Property is new construction
 * - rental: Property is a rental
 *
 * View features (true/false):
 * - city_view: Property has city views
 * - mountain_view: Property has mountain views
 * - park_view: Property has park views
 * - water_view: Property has water views
 *
 * Square footage filters:
 * - sqft_min: Minimum square footage
 * - sqft_max: Maximum square footage
 * - keywords: Comma-separated list of keywords to search in public remarks
 * - listing_id: MLS Number to search for (can be multiple, comma-separated)
 */
export const AdLandingPage = () => {
  const { config, isLoading: isTenantLoading } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();

  // Parse URL query parameters
  const queryParams = useMemo(() => {
    return new URLSearchParams(location.search);
  }, [location.search]);

  // Extract search parameters from URL
  const searchParams = useMemo(() => {
    // Get default city and state from config if available
    const defaultCity = config?.orgData?.client_address?.city || "";
    const defaultState = config?.orgData?.client_address?.state || "";

    const params = {
      query: queryParams.get("q") || "",
      title: queryParams.get("title") || "",
      // Don't fall back to the tenant's default city when a county-only search is
      // deep-linked (county in URL, no city) — that would re-pin it to one city.
      city:
        queryParams.get("city") ||
        (queryParams.get("county") ? "" : defaultCity),
      state: queryParams.get("state") || defaultState,
      zip_code: queryParams.get("zip_code") || undefined,
      county: queryParams.get("county") || undefined,
      bedrooms: queryParams.get("bedrooms") || undefined,
      bathrooms: queryParams.get("bathrooms") || undefined,
      min_price: queryParams.get("min_price")
        ? parseInt(queryParams.get("min_price") || "0")
        : undefined,
      max_price: queryParams.get("max_price")
        ? parseInt(queryParams.get("max_price") || "0")
        : undefined,
      property_type:
        queryParams.get("property_type") || "Single Family Residence",
      // Get all status values (URLSearchParams.getAll returns all values for a parameter)
      status:
        queryParams.getAll("status").length > 0
          ? queryParams.getAll("status")[0]
          : "Active",
      // Store all status values in an array
      statuses: queryParams.getAll("status"),
      // Get all subdivision values (URLSearchParams.getAll returns all values for a parameter)
      subdivision:
        queryParams.getAll("subdivision").length > 0
          ? queryParams.getAll("subdivision")[0]
          : "",
      // Store all subdivision values in an array
      subdivisions: queryParams.getAll("subdivision"),
      waterfront: queryParams.get("waterfront") === "true",
      pool: queryParams.get("pool") === "true",
      basement: queryParams.get("basement") === "true",
      garage: queryParams.get("garage") === "true",
      // New construction and rental filters
      newconstruction: queryParams.get("newconstruction") === "true",
      rental: queryParams.get("rental") === "true",
      // Additional view filters
      city_view: queryParams.get("city_view") === "true",
      mountain_view: queryParams.get("mountain_view") === "true",
      park_view: queryParams.get("park_view") === "true",
      water_view: queryParams.get("water_view") === "true",
      // Square footage filters
      sqft_min: queryParams.get("sqft_min")
        ? parseInt(queryParams.get("sqft_min") || "0")
        : undefined,
      sqft_max: queryParams.get("sqft_max")
        ? parseInt(queryParams.get("sqft_max") || "0")
        : undefined,
      // Parse keywords parameter (comma-separated list)
      keywords: queryParams.get("keywords")
        ? queryParams
            .get("keywords")
            ?.split(",")
            .map((k) => k.trim())
        : [],
      // Parse listing_id parameter (comma-separated list of MLS numbers)
      listing_ids: queryParams.get("listing_id")
        ? queryParams
            .get("listing_id")
            ?.split(",")
            .map((id) => id.trim())
        : [],
    };

    return params;
  }, [queryParams, config]);

  // Get view mode from URL parameter or default to combined
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const urlView = queryParams.get("view");
    if (urlView === "grid") {
      return "properties" as ViewMode;
    } else if (urlView === "map" || urlView === "combined") {
      return urlView as ViewMode;
    }
    return "combined";
  });

  // State for geocoded results (for map view)
  const [geocodedResults, setGeocodedResults] = useState<Property[]>([]);

  // State for display query
  const [displayQuery, setDisplayQuery] = useState(searchParams.query);

  // Determine if we should use direct search (no query) or regular search
  const isDirectSearch = !searchParams.query;

  // Track if we've already performed an initial search
  const initialSearchPerformedRef = useRef<boolean>(false);

  // Track if we've already fetched server properties to prevent infinite loops
  const serverPropertiesFetchedRef = useRef<boolean>(false);

  // Store server properties in a ref to ensure they're only created once
  const serverPropertiesRef = useRef<{
    properties: Property[];
    totalCount: number;
    hasProperties: boolean;
  }>({
    properties: [],
    totalCount: 0,
    hasProperties: false,
  });

  // Get server-fetched properties if available (only for direct search)
  // We'll only use this on the first render to avoid recreating server properties on every render
  const { serverProperties, serverTotalCount, hasServerProperties } =
    useServerFetchedProperties(isDirectSearch);

  // Store server properties in the ref on first render to avoid recreating them on every render
  if (hasServerProperties && !serverPropertiesFetchedRef.current) {
    console.log("Storing server properties in ref");
    serverPropertiesRef.current = {
      properties: serverProperties,
      totalCount: serverTotalCount,
      hasProperties: hasServerProperties,
    };
    serverPropertiesFetchedRef.current = true;
  }

  // Initialize search query hook based on whether we have a query or not
  const regularSearch = useSearchQuery();
  const directSearch = useDirectSearchQuery();

  // Log which search hook is being used
  console.log(
    `Using ${
      isDirectSearch ? "DIRECT SEARCH" : "REGULAR SEARCH"
    } hook. Query: "${searchParams.query}"`
  );

  // Use the appropriate search hook based on whether we have a query or not
  const {
    results: hookResults,
    loading: searchLoading,
    error: searchError,
    hasMore: hookHasMore,
    search,
    loadMore,
    filters,
    totalAvailable: hookTotalAvailable = 0,
    retryFailedRequest,
  } = isDirectSearch ? directSearch : regularSearch;

  // Determine if we should use server-fetched properties or hook results
  const results = useMemo(() => {
    // Use server-fetched properties if:
    // 1. We have server-fetched properties
    // 2. This is a direct search (no query)
    // 3. We haven't performed an initial search yet
    if (
      serverPropertiesRef.current.hasProperties &&
      isDirectSearch &&
      !initialSearchPerformedRef.current
    ) {
      return serverPropertiesRef.current.properties;
    }
    // Otherwise use hook results
    return hookResults;
  }, [isDirectSearch, hookResults, initialSearchPerformedRef]);

  // Determine total available count
  const totalAvailable = useMemo(() => {
    // Use server-fetched total count if:
    // 1. We have server-fetched properties
    // 2. This is a direct search (no query)
    // 3. We haven't performed an initial search yet
    if (
      serverPropertiesRef.current.hasProperties &&
      isDirectSearch &&
      !initialSearchPerformedRef.current
    ) {
      return serverPropertiesRef.current.totalCount;
    }
    // Otherwise use hook total available
    return hookTotalAvailable;
  }, [isDirectSearch, hookTotalAvailable, initialSearchPerformedRef]);

  // Determine if we have more results to load
  const hasMore = useMemo(() => {
    // If using server-fetched properties, check if we have more
    if (
      serverPropertiesRef.current.hasProperties &&
      isDirectSearch &&
      !initialSearchPerformedRef.current
    ) {
      return (
        serverPropertiesRef.current.properties.length <
        serverPropertiesRef.current.totalCount
      );
    }
    // Otherwise use hook hasMore
    return hookHasMore;
  }, [isDirectSearch, hookHasMore, initialSearchPerformedRef]);

  // Fetch MLS logos for all properties
  const { mlsLogosMap, mlsComplianceQueries } = useMLSLogos(results);

  // Create a custom loadMore function that handles server-fetched properties
  const handleLoadMore = useCallback(() => {
    // If we're using server-fetched properties, switch to direct search and load page 2
    if (
      serverPropertiesRef.current.hasProperties &&
      isDirectSearch &&
      !initialSearchPerformedRef.current
    ) {
      console.log(
        "Switching from server properties to direct search for page 2"
      );

      // Mark that we've performed an initial search
      initialSearchPerformedRef.current = true;

      // Use the utility function to build API filters
      const apiFilters = buildApiFilters(searchParams);

      // Use our utility function to load more properties after server-fetched properties
      // This will set the offset to 20 (page 2) and handle the transition from server to client
      loadMoreAfterServerProperties(
        directSearch,
        searchParams,
        serverPropertiesRef.current.properties,
        config?.id || ""
      );
    } else {
      // Use the regular loadMore function
      loadMore();
    }
  }, [
    isDirectSearch,
    initialSearchPerformedRef,
    searchParams,
    config?.id,
    directSearch,
    loadMore,
  ]);
  const filteredResults = useMemo(() => {
    return results;
  }, [results]);

  useEffect(() => {
    // Only process coordinates if we're in a map view mode to save resources on mobile
    if (viewMode === "map" || viewMode === "combined") {
      // First, filter out properties without latitude and longitude
      const validProperties = filteredResults.filter(
        (property) =>
          property.property?.latitude && property.property?.longitude
      );

      // Then map the remaining properties with valid coordinates
      const propertiesWithCoordinates = validProperties.map((property) => ({
        ...property,
        address: {
          ...property.address,
          coordinates: {
            lat: parseFloat(property.property?.latitude || "0"),
            lng: parseFloat(property.property?.longitude || "0"),
          },
        },
      }));

      setGeocodedResults(propertiesWithCoordinates);
    }
  }, [filteredResults, viewMode, isDirectSearch, initialSearchPerformedRef]);

  // Initialize search when component mounts or URL parameters change
  useMemo(() => {
    if (isTenantLoading || !config?.id) return;

    // Skip search if we have server-fetched properties and this is a direct search
    if (hasServerProperties && isDirectSearch) {
      console.log("Using server-fetched properties, skipping initial search");
      return;
    }

    // Mark that we've performed an initial search
    initialSearchPerformedRef.current = true;

    // City and state are now optional, using defaults from config if not provided

    try {
      // Use the utility function to build API filters
      const apiFilters = buildApiFilters(searchParams);

      // Execute search
      search({
        query: searchParams.query,
        tenant_id: config.id,
        location: {
          city: searchParams.city,
          state: searchParams.state.toUpperCase(),
        },
        filters: apiFilters,
        // Store original keywords in the search object for preservation during future searches
        keywords: searchParams.keywords,
      });

      // Track page view with analytics
      analyticsService.trackEvent({
        tenantId: config.id,
        eventType: "pageview",
        category: "Ad Landing Page",
        action: "View",
        userType: "visitor",
        data: {
          path: window.location.pathname + window.location.search,
          query: searchParams.query,
          city: searchParams.city,
          state: searchParams.state,
          title: document.title,
          referrer: document.referrer,
          tenantName: config.name || "Unknown",
          orgId: config?.id || "default",
          isAuthenticated: false,
        },
      });
    } catch (error) {
      // Handle error silently
    }
  }, [config?.id, searchParams, isTenantLoading, search]);

  // Handle search form submission
  const handleSearch = useCallback(
    (newFilters: SearchFilters) => {
      if (!config) return;

      // Mark that we've performed a search (so we don't use server properties anymore)
      initialSearchPerformedRef.current = true;

      // Clear server properties to ensure we start fresh
      serverPropertiesRef.current = {
        properties: [],
        totalCount: 0,
        hasProperties: false,
      };

      const orgId = config?.id || "";
      const tenantName = config.name || "Unknown";
      const searchQuery = newFilters.query || "";

      // Effective city for this search: the modal city, or "" for a county-wide
      // search when the city was cleared with a county set (else the page default).
      const effectiveCity = resolveSearchCity(newFilters, searchParams.city);

      // Determine if this is a direct search (no query) or regular search
      const isNewDirectSearch = !searchQuery;

      // Track search event
      analyticsService.trackEvent({
        tenantId: config.id,
        eventType: "search",
        category: "Property Search",
        action: "Execute Search",
        userType: "visitor",
        data: {
          searchQuery,
          searchFilters: newFilters,
          path: window.location.pathname + window.location.search,
          city: searchParams.city,
          state: searchParams.state,
          tenantName,
          orgId,
          isAuthenticated: false,
          isDirectSearch: isNewDirectSearch,
        },
      });

      // Update URL parameters to reflect the new search
      const updateURLParams = () => {
        const newParams = new URLSearchParams();

        // Add basic search parameters
        if (searchQuery) newParams.set("q", searchQuery);
        // Omit city for a county-wide search (effectiveCity is "" when the city
        // was cleared with a county set) so the server render also goes county-wide.
        if (effectiveCity) newParams.set("city", effectiveCity);
        newParams.set("state", searchParams.state);

        // Add ZIP code if it exists
        if (searchParams.zip_code)
          newParams.set("zip_code", searchParams.zip_code);

        // Add filter parameters if they exist in the new filters
        const filters = newFilters.filters || {};

        if (filters.bedrooms)
          newParams.set("bedrooms", filters.bedrooms.toString());
        if (filters.bathrooms)
          newParams.set("bathrooms", filters.bathrooms.toString());
        if (filters.min_price)
          newParams.set("min_price", filters.min_price.toString());
        if (filters.max_price)
          newParams.set("max_price", filters.max_price.toString());
        if (filters.property_sub_type)
          newParams.set("property_type", filters.property_sub_type);
        // Add all status values as separate parameters
        if (filters.standard_status) {
          if (Array.isArray(filters.standard_status)) {
            // If it's an array, add each status as a separate parameter
            filters.standard_status.forEach((status: string) => {
              newParams.append("status", status);
            });
          } else {
            // If it's a string, add it as a single parameter
            newParams.append("status", filters.standard_status);
          }
        }

        // Add title parameter (for h1 heading)
        if (searchQuery) newParams.set("title", searchQuery);

        // Add all subdivisions as separate parameters from filters
        // Use type assertion to access potential subdivision properties
        const filtersAny = filters as any;
        if (filtersAny.subdivision || filtersAny.subdivisions) {
          const subdivisionFilter =
            filtersAny.subdivision || filtersAny.subdivisions;
          if (Array.isArray(subdivisionFilter)) {
            // If it's an array, add each subdivision as a separate parameter
            subdivisionFilter.forEach((subdivision: string) => {
              newParams.append("subdivision", subdivision);
            });
          } else if (subdivisionFilter) {
            // If it's a string, add it as a single parameter
            newParams.append("subdivision", subdivisionFilter);
          }
        }

        // Add boolean features
        if (filters.waterfront) newParams.set("waterfront", "true");
        if (filters.pool) newParams.set("pool", "true");
        if (filters.basement) newParams.set("basement", "true");
        if (filters.garage) newParams.set("garage", "true");

        // Add new construction and rental filters
        if (filters.newconstructionyn) newParams.set("newconstruction", "true");
        if (filters.rentalyn) newParams.set("rental", "true");

        // Add view features
        if (filters.city_view) newParams.set("city_view", "true");
        if (filters.mountain_view) newParams.set("mountain_view", "true");
        if (filters.park_view) newParams.set("park_view", "true");
        if (filters.water_view) newParams.set("water_view", "true");

        // Add square footage
        if (filters.sqft_min)
          newParams.set("sqft_min", filters.sqft_min.toString());
        if (filters.sqft_max)
          newParams.set("sqft_max", filters.sqft_max.toString());

        // Add ZIP code if it exists in filters
        if (filters.zip_code)
          newParams.set("zip_code", filters.zip_code.toString());

        // Add county if it exists in filters (persists in URL + reaches server render)
        if (filters.county) newParams.set("county", filters.county.toString());

        // Add keywords if available
        // Use type assertion to access potential publicremarks property
        const filtersWithRemarks = filters as any;
        if (filtersWithRemarks.publicremarks) {
          const keywords = Array.isArray(filtersWithRemarks.publicremarks)
            ? filtersWithRemarks.publicremarks.join(",")
            : filtersWithRemarks.publicremarks;
          newParams.set("keywords", keywords);
        }

        // Preserve the current view mode
        const viewParamValue = viewMode === "properties" ? "grid" : viewMode;
        newParams.set("view", viewParamValue);

        // Get the URL string from URLSearchParams
        let urlString = newParams.toString();

        // Replace encoded commas (%2C) with actual commas for Google compatibility
        urlString = urlString.replace(/%2C/g, ",");

        // Update URL without reloading the page
        const newURL = `${window.location.pathname}?${urlString}`;
        window.history.pushState({ path: newURL }, "", newURL);

        // Force React Router to update the location
        navigate(newURL, { replace: true });
      };

      // Update URL parameters
      updateURLParams();

      // Use the appropriate search function based on whether a query is provided
      const searchFn = isNewDirectSearch
        ? directSearch.search
        : regularSearch.search;

      // Preserve keywords from original URL or from previous search
      const keywordsToPreserve =
        searchParams.keywords && searchParams.keywords.length > 0
          ? searchParams.keywords
          : (filters as any).keywords || [];

      // If we have keywords but they're not in the filters, add them
      if (keywordsToPreserve.length > 0 && !newFilters.filters?.publicremarks) {
        if (!newFilters.filters) newFilters.filters = {};
        (newFilters.filters as any).publicremarks = keywordsToPreserve;
      }

      // Create new filters object with search query and required parameters
      // Explicitly set initialServerProperties to undefined to ensure clean state
      searchFn({
        ...newFilters,
        query: searchQuery,
        tenant_id: config.id,
        location: {
          city: effectiveCity,
          state: searchParams.state.toUpperCase(),
        },
        // Store keywords for preservation
        keywords: keywordsToPreserve,
        initialServerProperties: undefined, // Clear any server properties from previous state
        offset: 0, // Reset to first page
      });
    },
    [regularSearch.search, directSearch.search, config, searchParams]
  );

  // Handle property click
  const handlePropertyClick = useCallback(
    (property: Property) => {
      if (!config) return;

      const orgId = config?.id || "default";
      const tenantName = config.name || "Unknown";

      // Track property click event
      analyticsService.trackEvent({
        tenantId: config.id,
        eventType: "interaction",
        category: "Property Interaction",
        action: "View Property",
        userType: "visitor",
        data: {
          propertyId: property.id,
          path: window.location.pathname + window.location.search,
          searchQuery: filters.query,
          searchFilters: filters,
          city: searchParams.city,
          state: searchParams.state,
          tenantName,
          orgId,
          isAuthenticated: false,
        },
      });

      // Open property in new tab with additional query parameters
      const tenantId = config.id || "";
      const mlsOsn = property.mlsBoardCode || "";
      window.open(
        `/property/${property.id}?tenant_id=${tenantId}&mls_osn=${mlsOsn}`,
        "_blank"
      );
    },
    [config, filters, searchParams]
  );

  // Handle view mode change
  const handleViewModeChange = useCallback(
    (mode: ViewMode) => {
      if (!config) return;

      const orgId = config?.id || "default";
      const tenantName = config.name || "Unknown";

      // Track view mode change
      analyticsService.trackEvent({
        tenantId: config.id,
        eventType: "interaction",
        category: "View Mode",
        action: "Change View",
        userType: "visitor",
        data: {
          previousMode: viewMode,
          newMode: mode,
          path: window.location.pathname + window.location.search,
          city: searchParams.city,
          state: searchParams.state,
          tenantName,
          orgId,
          isAuthenticated: false,
        },
      });

      // Update the URL with the new view mode
      const newParams = new URLSearchParams(location.search);

      // Map internal view mode names to URL parameter values
      const viewParamValue = mode === "properties" ? "grid" : mode;
      newParams.set("view", viewParamValue);

      // Get the URL string from URLSearchParams
      let urlString = newParams.toString();

      // Replace encoded commas (%2C) with actual commas for Google compatibility
      urlString = urlString.replace(/%2C/g, ",");

      // Update URL without reloading the page
      const newURL = `${window.location.pathname}?${urlString}`;
      window.history.pushState({ path: newURL }, "", newURL);

      setViewMode(mode);
    },
    [config, viewMode, searchParams, location.search]
  );

  // Loading state
  if (isTenantLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Generate human-readable filters, page title, and description
  const filterInfo = generateHumanReadableFilters(searchParams);
  // Generate page title and description
  const pageTitle = generatePageTitle(
    searchLoading,
    searchParams,
    totalAvailable,
    filterInfo
  );
  const pageDescription = generatePageDescription(
    searchLoading,
    searchParams,
    totalAvailable,
    filterInfo
  );

  return (
    <div className="p-6 lg:p-10 min-h-screen bg-gray-50">
      <div className="pb-8">
        <div className="flex flex-col max-w-[1600px] 3xl:max-w-[1800px] mx-auto">
          <div className="order-0 pt-8 pb-10l">
            {searchLoading ? (
              <>
                <div className="animate-pulse">
                  <div className="h-10 bg-gray-300 rounded w-fulll 2xl:w-1/2 mb-2 xl:mb-3"></div>
                  <div className="h-6 bg-gray-200 rounded w-full 2xl:w-3/4"></div>
                </div>
              </>
            ) : (
              <>
                <h1 className="text-2xl lg:text-3xl 2xl:text-4xl font-bold text-gray-900 mb-2 xl:mb-3">
                  {pageTitle}
                </h1>
                {/* 
                <p className="text-base lg:text-xl text-gray-700">
                  {pageDescription} Contact us for more information or to
                  schedule a viewing.
                </p>
                */}
              </>
            )}
          </div>

          <div className="order-1">
            <div
              className="py-3 sticky top-20 z-40 bg-gray-50 shadow-sm border-b"
              style={{
                borderColor: config?.branding?.colors?.text
                  ? `${config.branding.colors.text}11`
                  : "#00000011",
              }}
            >
              <div>
                <div className="flex flex-col lg:flex-row items-end lg:items-center lg:justify-between py-2 gap-2">
                  <div className="w-full">
                    <LandingSearchBox
                      onSearch={handleSearch}
                      initialFilters={
                        // If using server properties, create filters directly from URL parameters
                        hasServerProperties &&
                        isDirectSearch &&
                        !initialSearchPerformedRef.current
                          ? {
                              query: displayQuery,
                              filters: buildApiFilters(searchParams),
                              location: {
                                city: searchParams.city,
                                state: searchParams.state.toUpperCase(),
                              },
                              tenant_id: config?.id || "",
                            }
                          : { ...filters, query: displayQuery }
                      }
                      isLoading={searchLoading}
                      onQueryChange={setDisplayQuery}
                      showFilters={true}
                      filterBorderColor={config?.branding?.colors?.primary}
                      filterTextColor={config?.branding?.colors?.primary}
                    />
                  </div>

                  <div className="flex justify-end lg:justify-start">
                    <ViewToggle
                      currentView={viewMode}
                      onViewChange={handleViewModeChange}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-1 relative overflow-hidden mt-5 lg:mt-12">
              {viewMode === "properties" && (
                <div className="w-full">
                  <SearchResults
                    results={filteredResults}
                    loading={searchLoading}
                    error={searchError}
                    hasMore={hasMore}
                    onLoadMore={handleLoadMore}
                    onPropertyClick={handlePropertyClick}
                    totalAvailable={totalAvailable}
                    onRetry={retryFailedRequest}
                    filters={filters?.filters}
                    searchQuery={filters?.query}
                    forceShowScores={false}
                    pageTitle={pageTitle}
                    hideGoogleSignIn={true}
                    mlsLogosMap={mlsLogosMap}
                    mlsComplianceQueries={mlsComplianceQueries}
                  />
                </div>
              )}

              {viewMode === "map" && (
                <div className="w-full h-[700px]">
                  <Suspense
                    fallback={
                      <div className="flex justify-center items-center h-full bg-gray-100">
                        <Loader className="w-8 h-8 animate-spin text-primary" />
                      </div>
                    }
                  >
                    <MapView
                      properties={geocodedResults}
                      onPropertySelect={handlePropertyClick}
                      isLoading={searchLoading}
                      mlsLogosMap={mlsLogosMap}
                    />
                  </Suspense>
                </div>
              )}

              {viewMode === "combined" && (
                <div className="flex flex-col md:flex-row gap-8 relative">
                  <div
                    className="w-full md:overflow-y-auto md:max-h-[calc(100vh-180px)] md:pr-4 md:scrollbar-thin md:scrollbar-thumb-gray-300 md:scrollbar-track-gray-100 md:hover:scrollbar-thumb-gray-400"
                    style={{
                      borderColor: config?.branding?.colors?.text
                        ? `${config.branding.colors.text}11`
                        : "#00000011",
                    }}
                  >
                    <SearchResults
                      results={filteredResults}
                      loading={searchLoading}
                      error={searchError}
                      hasMore={hasMore}
                      onLoadMore={handleLoadMore}
                      onPropertyClick={handlePropertyClick}
                      totalAvailable={totalAvailable}
                      onRetry={retryFailedRequest}
                      gridColumns={2}
                      filters={filters?.filters}
                      searchQuery={filters?.query}
                      forceShowScores={false}
                      pageTitle={pageTitle}
                      hideGoogleSignIn={true}
                      mlsLogosMap={mlsLogosMap}
                      mlsComplianceQueries={mlsComplianceQueries}
                    />
                  </div>
                  <div className="w-full h-[calc(100vh-180px)] md:sticky md:top-[180px] bg-white rounded-lg shadow-sm">
                    <Suspense
                      fallback={
                        <div className="flex justify-center items-center h-full bg-gray-100">
                          <Loader className="w-8 h-8 animate-spin text-primary" />
                        </div>
                      }
                    >
                      <MapView
                        properties={geocodedResults}
                        onPropertySelect={handlePropertyClick}
                        isLoading={searchLoading}
                        mlsLogosMap={mlsLogosMap}
                      />
                    </Suspense>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdLandingPage;
